import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CraftRequest, CraftingGrid, CraftResponse, GeneratedItem, ItemCategory, ItemId, ItemMeta, RecipeRecord, SlotIndex, TextureId } from '@/types/domain';
import { normalize, makeKey, suggestType, type NormalizedRecipe } from '@/engine/RecipeNormalizer';
import { vanillaRecipeEngine } from '@/engine/VanillaRecipeEngine';
import { customRecipeEngine } from '@/engine/CustomRecipeEngine';
import { findDuplicate } from '@/engine/DuplicateDetector';
import { textureCatalog } from '@/engine/TextureCatalog';
import { itemRegistry } from '@/engine/VanillaItemRegistry';
import { generatedToMeta, resolveMeta, textureUrl } from './itemHelpers';
import { SaveManager, type HistoryEntry } from '@/services/SaveManager';
import { GeneratedItemRepository } from '@/services/GeneratedItemRepository';
import { RecipeRepository } from '@/services/RecipeRepository';
import { craft as apiCraft, CraftError, fetchConfig } from '@/services/OpenRouterService';
import { DEFAULT_SETTINGS, type GameSettings } from './settings';
import { sfx, setSoundEnabled } from './sound';
import { recipeRecordId } from '@/utils/id';

export type ResultState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'vanilla'; itemId: ItemId; count: number; name: string }
  | { kind: 'custom'; item: GeneratedItem; cached: boolean }
  | { kind: 'invalid'; reason: string; suggestion: string }
  | { kind: 'error'; message: string };

export interface GameApi {
  ready: boolean;
  serverMock: boolean;
  grid: CraftingGrid;
  result: ResultState;
  preview: ResultState;
  discovered: GeneratedItem[];
  favorites: ItemId[];
  history: HistoryEntry[];
  settings: GameSettings;
  inventory: Record<ItemId, number>;
  cooldownRemaining: number;
  rerollLockTexture: TextureId | undefined;
  rerollLockCategory: ItemCategory | undefined;
  discoveryCount: number;
  devInfo: { lastRequest?: CraftRequest; lastResponse?: CraftResponse; lastKey?: string; lastNorm?: NormalizedRecipe };

  placeItem: (slot: SlotIndex, itemId: ItemId) => void;
  placeItemAnySlot: (itemId: ItemId) => void;
  clearSlot: (slot: SlotIndex) => void;
  clearGrid: () => void;
  craft: () => Promise<void>;
  reroll: () => Promise<void>;
  collect: (itemId: ItemId, count?: number) => void;
  toggleFavorite: (itemId: ItemId) => void;
  updateSettings: (patch: Partial<GameSettings>) => void;
  setRerollLock: (texture?: TextureId, category?: ItemCategory) => void;
  exportSave: () => Promise<string>;
  importSave: (json: string) => Promise<void>;
  resetSave: () => Promise<void>;
  clearCache: () => Promise<{ cleared: number }>;
  clearInventory: () => void;
  loadSeeds: () => Promise<number>;
  clearDiscovered: () => Promise<void>;
  metaFor: (id: ItemId) => ItemMeta | undefined;
  urlFor: (id: ItemId) => string | undefined;
  reloadAll: () => Promise<void>;
}

const emptyGrid = (): CraftingGrid => Array(9).fill(null);

export function useGame(): GameApi {
  const [ready, setReady] = useState(false);
  const [grid, setGrid] = useState<CraftingGrid>(emptyGrid);
  const [result, setResult] = useState<ResultState>({ kind: 'idle' });
  const [discovered, setDiscovered] = useState<GeneratedItem[]>([]);
  const [favorites, setFavorites] = useState<ItemId[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [settings, setSettings] = useState<GameSettings>(DEFAULT_SETTINGS);
  const [inventory, setInventory] = useState<Record<ItemId, number>>({});
  const [serverMock, setServerMock] = useState(true);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [lockTexture, setLockTexture] = useState<TextureId | undefined>(undefined);
  const [lockCategory, setLockCategory] = useState<ItemCategory | undefined>(undefined);
  const [devInfo, setDevInfo] = useState<{ lastRequest?: CraftRequest; lastResponse?: CraftResponse; lastKey?: string; lastNorm?: NormalizedRecipe }>({});

  const recipeCache = useRef(new Map<string, RecipeRecord>());
  const discoveredRef = useRef<GeneratedItem[]>([]);
  discoveredRef.current = discovered;

  const chosenType = useCallback(
    (g: CraftingGrid) => (settings.interpretation === 'auto' ? suggestType(g) : settings.interpretation),
    [settings.interpretation],
  );

  const metaFor = useCallback((id: ItemId) => resolveMeta(id), []);
  const urlFor = useCallback((id: ItemId) => (id.startsWith('custom:') ? textureUrl(metaFor(id)?.textureId || 'minecraft:stone') : textureCatalog.urlFor(id)), [metaFor]);

  const reloadAll = useCallback(async () => {
    const items = await GeneratedItemRepository.all();
    setDiscovered(items);
    discoveredRef.current = items;
    itemRegistry.allVanilla();
    for (const it of items) itemRegistry.register(generatedToMeta(it));
    const recs = await RecipeRepository.all();
    recipeCache.current = new Map(recs.map((r) => [r.key, r]));
    setFavorites(await SaveManager.favorites());
    setHistory(await SaveManager.allHistory());
    const inv = (await SaveManager.getSetting<Record<ItemId, number>>('inventory', {})) || {};
    setInventory(inv);
  }, []);

  // init
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await SaveManager.init();
      const saved = { ...DEFAULT_SETTINGS, ...(await SaveManager.getSetting<Partial<GameSettings>>('settings', {})) };
      await reloadAll();
      const cfg = await fetchConfig();
      if (cancelled) return;
      // The server is the authority on the configured model: sync the persisted
      // setting from the server config so .env OPENROUTER_MODEL is respected.
      const synced = { ...saved, model: cfg.model || saved.model };
      setSettings(synced);
      setSoundEnabled(synced.sound);
      setServerMock(cfg.mock || synced.mockAi);
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadAll]);

  // cooldown ticker
  useEffect(() => {
    if (cooldownUntil === 0) {
      setCooldownRemaining(0);
      return;
    }
    const tick = () => {
      const rem = Math.max(0, cooldownUntil - Date.now());
      setCooldownRemaining(rem);
      if (rem <= 0) setCooldownUntil(0);
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  // live preview (vanilla first, then cache). No network calls.
  const preview = useMemo<ResultState>(() => {
    if (result.kind === 'loading') return { kind: 'idle' };
    if (!grid.some((c) => c)) return { kind: 'idle' };
    const vanilla = vanillaRecipeEngine.match(grid);
    if (vanilla.matched && vanilla.result) {
      const meta = itemRegistry.get(vanilla.result.item);
      return { kind: 'vanilla', itemId: vanilla.result.item, count: vanilla.result.count, name: meta?.name || vanilla.result.item };
    }
    const type = chosenType(grid);
    const norm = normalize(grid, type);
    const key = makeKey(norm, type);
    const cached = recipeCache.current.get(key);
    if (cached) {
      if (cached.resultItem) return { kind: 'custom', item: cached.resultItem, cached: true };
      if (cached.invalidReason)
        return { kind: 'invalid', reason: cached.invalidReason, suggestion: cached.invalidSuggestion || '' };
    }
    return { kind: 'idle' };
  }, [grid, result.kind, chosenType]);

  const pushHistory = useCallback(async (entry: Omit<HistoryEntry, 'id'>) => {
    const full: HistoryEntry = { ...entry, id: recipeRecordId() };
    await SaveManager.addHistory(full);
    setHistory(await SaveManager.allHistory());
  }, []);

  const placeItem = useCallback((slot: SlotIndex, itemId: ItemId) => {
    setGrid((g) => {
      const next = g.slice();
      next[slot] = { itemId, count: 1 };
      return next;
    });
    setResult({ kind: 'idle' });
    if (settings.sound) sfx.place();
  }, [settings.sound]);

  const placeItemAnySlot = useCallback((itemId: ItemId) => {
    setGrid((g) => {
      const next = g.slice();
      const idx = next.findIndex((c) => c === null);
      if (idx >= 0) next[idx] = { itemId, count: 1 };
      return next;
    });
    setResult({ kind: 'idle' });
    if (settings.sound) sfx.place();
  }, [settings.sound]);

  const clearSlot = useCallback((slot: SlotIndex) => {
    setGrid((g) => {
      const next = g.slice();
      next[slot] = null;
      return next;
    });
    setResult({ kind: 'idle' });
    if (settings.sound) sfx.remove();
  }, [settings.sound]);

  const clearGrid = useCallback(() => {
    setGrid(emptyGrid());
    setResult({ kind: 'idle' });
    if (settings.sound) sfx.clear();
  }, [settings.sound]);

  const craft = useCallback(async () => {
    if (!grid.some((c) => c)) return;
    const vanilla = vanillaRecipeEngine.match(grid);
    if (vanilla.matched && vanilla.result) {
      if (settings.sound) sfx.craft();
      const meta = itemRegistry.get(vanilla.result.item);
      setResult({ kind: 'vanilla', itemId: vanilla.result.item, count: vanilla.result.count, name: meta?.name || vanilla.result.item });
      return;
    }
    const type = chosenType(grid);
    const norm = normalize(grid, type);
    const key = makeKey(norm, type);
    const cached = recipeCache.current.get(key);
    if (cached) {
      if (settings.sound) sfx.craft();
      if (cached.resultItem) setResult({ kind: 'custom', item: cached.resultItem, cached: true });
      else if (cached.invalidReason) setResult({ kind: 'invalid', reason: cached.invalidReason, suggestion: cached.invalidSuggestion || '' });
      return;
    }
    // unknown -> call AI
    setResult({ kind: 'loading' });
    const ingredients = norm.ingredientIds.map((id) => resolveMeta(id)).filter((m): m is ItemMeta => !!m);
    const request = customRecipeEngine.buildRequest(grid, {
      ingredients,
      recentDiscoveries: discoveredRef.current,
      recipeType: type,
      progressionStrictness: settings.strictness,
      model: settings.mockAi ? undefined : settings.model,
      lockTextureId: lockTexture,
      lockCategory: lockCategory,
    });
    request.mock = settings.mockAi;
    setDevInfo({ lastRequest: request, lastKey: key, lastNorm: norm });
    try {
      const { response, model } = await apiCraft(request);
      setDevInfo((d) => ({ ...d, lastResponse: response }));
      if (response.status === 'invalid') {
        const record: RecipeRecord = {
          key, shapedKey: norm.shapedKey, shapelessKey: norm.shapelessKey, type, ingredientIds: norm.ingredientIds, grid,
          resultId: null, isVanilla: false, invalidReason: response.reason, invalidSuggestion: response.suggestion, createdAt: Date.now(),
        };
        await RecipeRepository.save(record);
        recipeCache.current.set(key, record);
        setResult({ kind: 'invalid', reason: response.reason, suggestion: response.suggestion });
        await pushHistory({ at: Date.now(), key, resultId: null, isVanilla: false, label: `Invalid: ${response.reason.slice(0, 60)}` });
        if (settings.sound) sfx.error();
        return;
      }
      // valid
      const candidates = request.textureCandidates;
      const dup = findDuplicate(discoveredRef.current, response.name, response.tags, norm.ingredientIds);
      let item: GeneratedItem;
      if (dup.isDuplicate && dup.existing) {
        item = dup.existing;
        const record: RecipeRecord = {
          key, shapedKey: norm.shapedKey, shapelessKey: norm.shapelessKey, type, ingredientIds: norm.ingredientIds, grid,
          resultId: item.id, resultItem: item, isVanilla: false, createdAt: Date.now(),
        };
        await RecipeRepository.save(record);
        recipeCache.current.set(key, record);
      } else {
        const parentKeys = new Map<ItemId, string>();
        for (const id of norm.ingredientIds) {
          if (id.startsWith('custom:')) {
            // find the recipe record whose resultId === id
            for (const r of recipeCache.current.values()) if (r.resultId === id && r.key) parentKeys.set(id, r.key);
          }
        }
        item = customRecipeEngine.materialize(grid, response, ingredients, candidates, parentKeys, settings.strictness);
        item.model = model;
        await GeneratedItemRepository.save(item);
        itemRegistry.register(generatedToMeta(item));
        const record: RecipeRecord = {
          key, shapedKey: norm.shapedKey, shapelessKey: norm.shapelessKey, type, ingredientIds: norm.ingredientIds, grid,
          resultId: item.id, resultItem: item, isVanilla: false, createdAt: Date.now(),
        };
        await RecipeRepository.save(record);
        recipeCache.current.set(key, record);
        setDiscovered((d) => [item, ...d]);
        discoveredRef.current = [item, ...discoveredRef.current];
      }
      setResult({ kind: 'custom', item, cached: false });
      await pushHistory({ at: Date.now(), key, resultId: item.id, isVanilla: false, label: item.name });
      if (settings.sound) sfx.craft();
    } catch (e) {
      const message = e instanceof CraftError ? e.message : 'AI generation failed';
      setResult({ kind: 'error', message });
      if (settings.sound) sfx.error();
    }
  }, [grid, chosenType, settings.strictness, settings.model, settings.mockAi, settings.sound, lockTexture, lockCategory, pushHistory]);

  const reroll = useCallback(async () => {
    if (result.kind !== 'custom' || cooldownRemaining > 0) return;
    // remember previous in history
    setCooldownUntil(Date.now() + settings.rerollCooldownMs);
    setResult({ kind: 'loading' });
    const type = chosenType(grid);
    const norm = normalize(grid, type);
    const ingredients = norm.ingredientIds.map((id) => resolveMeta(id)).filter((m): m is ItemMeta => !!m);
    const request = customRecipeEngine.buildRequest(grid, {
      ingredients,
      recentDiscoveries: discoveredRef.current,
      recipeType: type,
      progressionStrictness: settings.strictness,
      model: settings.mockAi ? undefined : settings.model,
      lockTextureId: lockTexture,
      lockCategory: lockCategory,
    });
    request.mock = settings.mockAi;
    setDevInfo({ lastRequest: request, lastKey: makeKey(norm, type), lastNorm: norm });
    try {
      const { response, model } = await apiCraft(request);
      setDevInfo((d) => ({ ...d, lastResponse: response }));
      if (response.status === 'valid') {
        const candidates = request.textureCandidates;
        const item = customRecipeEngine.materialize(grid, response, ingredients, candidates, new Map(), settings.strictness);
        item.model = model;
        await GeneratedItemRepository.save(item);
        itemRegistry.register(generatedToMeta(item));
        setDiscovered((d) => [item, ...d]);
        discoveredRef.current = [item, ...discoveredRef.current];
        setResult({ kind: 'custom', item, cached: false });
        await pushHistory({ at: Date.now(), key: makeKey(norm, type) + ':reroll', resultId: item.id, isVanilla: false, label: `Reroll -> ${item.name}` });
        if (settings.sound) sfx.craft();
      } else {
        setResult({ kind: 'invalid', reason: response.reason, suggestion: response.suggestion });
      }
    } catch (e) {
      setResult({ kind: 'error', message: e instanceof CraftError ? e.message : 'Reroll failed' });
      if (settings.sound) sfx.error();
    }
  }, [result.kind, cooldownRemaining, grid, chosenType, settings, lockTexture, lockCategory, pushHistory]);

  const collect = useCallback((itemId: ItemId, count = 1) => {
    setInventory((inv) => {
      const next = { ...inv, [itemId]: (inv[itemId] || 0) + count };
      void SaveManager.setSetting('inventory', next);
      return next;
    });
    if (settings.sound) sfx.craft();
  }, [settings.sound]);

  const toggleFavorite = useCallback(async (itemId: ItemId) => {
    setFavorites((f) => {
      if (f.includes(itemId)) {
        void SaveManager.removeFavorite(itemId);
        return f.filter((x) => x !== itemId);
      }
      void SaveManager.addFavorite(itemId);
      return [itemId, ...f];
    });
    if (settings.sound) sfx.click();
  }, [settings.sound]);

  const updateSettings = useCallback((patch: Partial<GameSettings>) => {
    setSettings((s) => {
      const next = { ...s, ...patch };
      void SaveManager.setSetting('settings', next);
      if (patch.sound !== undefined) setSoundEnabled(next.sound);
      if (patch.mockAi !== undefined) setServerMock(next.mockAi || serverMock);
      return next;
    });
  }, [serverMock]);

  const setRerollLock = useCallback((texture?: TextureId, category?: ItemCategory) => {
    setLockTexture(texture);
    setLockCategory(category);
  }, []);

  const exportSave = useCallback(async () => {
    const bundle = await SaveManager.exportSave();
    return JSON.stringify(bundle, null, 2);
  }, []);

  const importSave = useCallback(async (json: string) => {
    const data = JSON.parse(json);
    await SaveManager.importSave(data);
    await reloadAll();
  }, [reloadAll]);

  const resetSave = useCallback(async () => {
    await SaveManager.resetSave();
    setGrid(emptyGrid());
    setResult({ kind: 'idle' });
    await reloadAll();
  }, [reloadAll]);

  const clearCache = useCallback(async (): Promise<{ cleared: number }> => {
    const res = await RecipeRepository.clearCache();
    recipeCache.current = new Map((await RecipeRepository.all()).map((r) => [r.key, r]));
    if (settings.sound) sfx.clear();
    return res;
  }, [settings.sound]);

  const clearInventory = useCallback(() => {
    setInventory({});
    void SaveManager.setSetting('inventory', {});
    if (settings.sound) sfx.clear();
  }, [settings.sound]);

  const loadSeeds = useCallback(async (): Promise<number> => {
    const res = await SaveManager.loadSeeds();
    await reloadAll();
    if (settings.sound) sfx.craft();
    return res.items;
  }, [reloadAll, settings.sound]);

  const clearDiscovered = useCallback(async (): Promise<void> => {
    const items = await SaveManager.allItems();
    for (const it of items) await SaveManager.deleteItem(it.id);
    const recipes = await SaveManager.allRecipes();
    for (const r of recipes) if (!r.isVanilla) await SaveManager.deleteRecipe(r.key);
    await SaveManager.clearHistory();
    recipeCache.current = new Map((await RecipeRepository.all()).map((r) => [r.key, r]));
    setDiscovered([]);
    setHistory([]);
    if (settings.sound) sfx.clear();
  }, [settings.sound]);

  return {
    ready,
    serverMock,
    grid,
    result,
    preview,
    discovered,
    favorites,
    history,
    settings,
    inventory,
    cooldownRemaining,
    rerollLockTexture: lockTexture,
    rerollLockCategory: lockCategory,
    discoveryCount: discovered.length,
    devInfo,
    placeItem,
    placeItemAnySlot,
    clearSlot,
    clearGrid,
    craft,
    reroll,
    collect,
    toggleFavorite,
    updateSettings,
    setRerollLock,
    exportSave,
    importSave,
    resetSave,
    clearCache,
    clearInventory,
    loadSeeds,
    clearDiscovered,
    metaFor,
    urlFor,
    reloadAll,
  };
}
